// WbHaulierDetailPage — /weighbridge/hauliers/:enc_id (and /new).
//
// Phase C3 Group 1 + post-C13 cleanup. Create + edit form for a single
// haulier, plus two read-only sub-tabs (Vehicles, Drivers) that hang
// the related collections off the same page. The sub-tabs only render
// when editing an existing haulier (there's no enc_id yet for `new`).

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Badge } from '../../../components/ui/Badge/Badge';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import {
  useCreateHaulier,
  useDeleteHaulier,
  useUpdateHaulier,
  useWbHaulier,
  type CreateHaulierInput,
  type LicenceExpiryStatus,
} from '../hooks/useWbHauliers';
import { useWbVehicles, type TareStatus } from '../hooks/useWbVehicles';
import { useWbDrivers } from '../hooks/useWbDrivers';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbHaulierDetailPage.module.css';

// Simple permissive email regex — duplicates browser-level validation
// (type="email") but catches the "user pasted whitespace" edge case
// before we POST. Server is the canonical validator (zod email).
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FormState {
  name: string;
  code: string;
  contact_name: string;
  phone: string;
  email: string;
  address: string;
  carrier_licence_number: string;
  carrier_licence_expiry: string;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  code: '',
  contact_name: '',
  phone: '',
  email: '',
  address: '',
  carrier_licence_number: '',
  carrier_licence_expiry: '',
  is_active: true,
};

function LicenceChip({ status }: { status: LicenceExpiryStatus }): JSX.Element {
  switch (status) {
    case 'valid':
      return <Badge variant="success">Valid</Badge>;
    case 'expiring_soon':
      return <Badge variant="warning">Expiring soon</Badge>;
    case 'expired':
      return <Badge variant="danger">Expired</Badge>;
    case 'not_set':
    default:
      return <Badge variant="muted">Not set</Badge>;
  }
}

export function WbHaulierDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const { enc_id } = useParams<{ enc_id: string }>();
  const isNew = enc_id === 'new' || enc_id === undefined;

  const haulierQuery = useWbHaulier(isNew ? undefined : enc_id);
  const createMut = useCreateHaulier();
  const updateMut = useUpdateHaulier(isNew ? '' : (enc_id as string));
  const deleteMut = useDeleteHaulier(isNew ? '' : (enc_id as string));

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [licenceStatus, setLicenceStatus] = useState<LicenceExpiryStatus | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Hydrate the form once the GET resolves.
  useEffect(() => {
    if (isNew) return;
    const h = haulierQuery.data?.haulier;
    if (!h) return;
    setForm({
      name: h.name,
      code: h.code,
      contact_name: h.contact_name ?? '',
      phone: h.phone ?? '',
      email: h.email ?? '',
      address: h.address ?? '',
      carrier_licence_number: h.carrier_licence_number ?? '',
      carrier_licence_expiry: h.carrier_licence_expiry ?? '',
      is_active: h.is_active,
    });
    setLicenceStatus(h.licence_expiry_status ?? null);
  }, [isNew, haulierQuery.data]);

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs['name'] = 'Name is required.';
    if (form.name.length > 200) errs['name'] = 'Name must be 200 characters or fewer.';
    if (!form.code.trim()) errs['code'] = 'Code is required.';
    if (form.code.length > 50) errs['code'] = 'Code must be 50 characters or fewer.';
    const emailTrim = form.email.trim();
    if (emailTrim && !EMAIL_REGEX.test(emailTrim)) {
      errs['email'] = 'Enter a valid email address.';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function nullIfEmpty(s: string): string | null {
    const t = s.trim();
    return t === '' ? null : t;
  }

  function buildBody(): CreateHaulierInput {
    return {
      name: form.name.trim(),
      code: form.code.trim(),
      contact_name: nullIfEmpty(form.contact_name),
      phone: nullIfEmpty(form.phone),
      email: nullIfEmpty(form.email),
      address: nullIfEmpty(form.address),
      carrier_licence_number: nullIfEmpty(form.carrier_licence_number),
      carrier_licence_expiry: nullIfEmpty(form.carrier_licence_expiry),
      is_active: form.is_active,
    };
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!validate()) return;
    const body = buildBody();
    if (isNew) {
      createMut.mutate(body, {
        onSuccess: () => navigate('/weighbridge/hauliers'),
        onError: (err) => setError(extractErrorMessage(err)),
      });
    } else {
      updateMut.mutate(body, {
        onSuccess: () => navigate('/weighbridge/hauliers'),
        onError: (err) => setError(extractErrorMessage(err)),
      });
    }
  }

  function handleDeleteConfirm() {
    deleteMut.mutate(undefined, {
      onSuccess: () => {
        setConfirmOpen(false);
        navigate('/weighbridge/hauliers');
      },
      onError: (err) => {
        setConfirmOpen(false);
        setError(extractErrorMessage(err));
      },
    });
  }

  const isSaving = createMut.isPending || updateMut.isPending;
  const isLoadingExisting = !isNew && haulierQuery.isLoading;

  return (
    <div className={styles.page}>
      <PageHeader
        title={isNew ? 'New Haulier' : form.name || 'Edit Haulier'}
        subtitle={
          isNew ? 'Create a new haulier' : 'Edit haulier details'
        }
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate('/weighbridge/hauliers')}
          >
            Back to Hauliers
          </Button>
        }
      />

      {isLoadingExisting ? (
        <Card>
          <div className={styles.loading}>
            <Spinner label="Loading haulier" />
          </div>
        </Card>
      ) : (
        <form onSubmit={handleSubmit} className={styles.form}>
          <Card>
            {/* -- Identity ----------------------------------------- */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Identity</h3>
              <div className={styles.grid}>
                <label className={styles.field}>
                  <span>Name</span>
                  <input
                    type="text"
                    required
                    maxLength={200}
                    value={form.name}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                  />
                  {fieldErrors['name'] ? (
                    <span className={styles.fieldError}>
                      {fieldErrors['name']}
                    </span>
                  ) : null}
                </label>

                <label className={styles.field}>
                  <span>Code</span>
                  <input
                    type="text"
                    required
                    maxLength={50}
                    value={form.code}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, code: e.target.value }))
                    }
                    onBlur={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        code: e.target.value.toUpperCase(),
                      }))
                    }
                  />
                  {fieldErrors['code'] ? (
                    <span className={styles.fieldError}>
                      {fieldErrors['code']}
                    </span>
                  ) : null}
                </label>
              </div>
            </div>

            {/* -- Contact ------------------------------------------ */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Contact</h3>
              <div className={styles.grid}>
                <label className={styles.field}>
                  <span>Contact name</span>
                  <input
                    type="text"
                    maxLength={200}
                    value={form.contact_name}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        contact_name: e.target.value,
                      }))
                    }
                  />
                </label>

                <label className={styles.field}>
                  <span>Phone</span>
                  <input
                    type="tel"
                    maxLength={30}
                    value={form.phone}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, phone: e.target.value }))
                    }
                  />
                </label>

                <label className={styles.field}>
                  <span>Email</span>
                  <input
                    type="email"
                    maxLength={255}
                    value={form.email}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                  />
                  {fieldErrors['email'] ? (
                    <span className={styles.fieldError}>
                      {fieldErrors['email']}
                    </span>
                  ) : null}
                </label>
              </div>
            </div>

            {/* -- Address ------------------------------------------ */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Address</h3>
              <label className={styles.field}>
                <span>Address</span>
                <textarea
                  rows={3}
                  maxLength={2000}
                  value={form.address}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, address: e.target.value }))
                  }
                  placeholder="Optional"
                />
              </label>
            </div>

            {/* -- Carrier Licence --------------------------------- */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Carrier Licence</h3>
              <div className={styles.grid}>
                <label className={styles.field}>
                  <span>Licence number</span>
                  <input
                    type="text"
                    maxLength={100}
                    value={form.carrier_licence_number}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        carrier_licence_number: e.target.value,
                      }))
                    }
                  />
                </label>

                <div className={styles.field}>
                  <span>Licence expiry</span>
                  <div className={styles.licenceRowInner}>
                    <input
                      type="date"
                      value={form.carrier_licence_expiry}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          carrier_licence_expiry: e.target.value,
                        }))
                      }
                    />
                    {licenceStatus ? (
                      <LicenceChip status={licenceStatus} />
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            {/* -- Status ------------------------------------------- */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Status</h3>
              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      is_active: e.target.checked,
                    }))
                  }
                />
                <span>Active</span>
              </label>
            </div>
          </Card>

          {error ? <p className={styles.error}>{error}</p> : null}

          <div className={styles.actions}>
            <div>
              {!isNew ? (
                <Button
                  type="button"
                  variant="danger"
                  icon={Trash2}
                  onClick={() => setConfirmOpen(true)}
                  disabled={isSaving}
                >
                  Delete
                </Button>
              ) : null}
            </div>
            <div className={styles.rightActions}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/weighbridge/hauliers')}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={isSaving}>
                {isNew ? 'Create Haulier' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </form>
      )}

      {!isNew && enc_id !== undefined ? (
        <HaulierRelatedTabs haulierEncId={enc_id} />
      ) : null}

      <ConfirmModal
        open={confirmOpen}
        title="Delete haulier?"
        message={
          <p>
            Delete <strong>{form.name || 'this haulier'}</strong>? This cannot
            be undone.
          </p>
        }
        variant="danger"
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmOpen(false)}
        loading={deleteMut.isPending}
      />
    </div>
  );
}

// -- Sub-tabs ----------------------------------------------------------

type SubTab = 'vehicles' | 'drivers';

function HaulierRelatedTabs({
  haulierEncId,
}: {
  haulierEncId: string;
}): JSX.Element {
  const [tab, setTab] = useState<SubTab>('vehicles');
  return (
    <Card>
      <div className={styles.tabs} role="tablist">
        <button
          type="button"
          className={styles.tab}
          role="tab"
          aria-selected={tab === 'vehicles'}
          onClick={() => setTab('vehicles')}
          data-testid="haulier-tab-vehicles"
        >
          Vehicles
        </button>
        <button
          type="button"
          className={styles.tab}
          role="tab"
          aria-selected={tab === 'drivers'}
          onClick={() => setTab('drivers')}
          data-testid="haulier-tab-drivers"
        >
          Drivers
        </button>
      </div>
      {tab === 'vehicles' ? (
        <HaulierVehiclesTab haulierEncId={haulierEncId} />
      ) : (
        <HaulierDriversTab haulierEncId={haulierEncId} />
      )}
    </Card>
  );
}

function tareLabel(status: TareStatus): string {
  switch (status) {
    case 'fresh':
      return 'Fresh';
    case 'stale':
      return 'Stale';
    case 'not_set':
    default:
      return 'Not set';
  }
}

function HaulierVehiclesTab({
  haulierEncId,
}: {
  haulierEncId: string;
}): JSX.Element {
  const q = useWbVehicles({ haulier_enc_id: haulierEncId, per_page: 100 });
  if (q.isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner label="Loading vehicles" />
      </div>
    );
  }
  if (q.isError) {
    return <p className={styles.empty}>Could not load vehicles.</p>;
  }
  const rows = q.data?.vehicles ?? [];
  if (rows.length === 0) {
    return (
      <p className={styles.empty} data-testid="haulier-vehicles-empty">
        No vehicles linked to this haulier.
      </p>
    );
  }
  return (
    <table className={styles.subTable} data-testid="haulier-vehicles-table">
      <thead>
        <tr>
          <th>VRM</th>
          <th>Type</th>
          <th>Tare status</th>
          <th>Active</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((v) => (
          <tr key={v.enc_id}>
            <td>
              <Link to={`/weighbridge/vehicles/${v.enc_id}`}>
                {v.registration}
              </Link>
            </td>
            <td>{v.vehicle_type}</td>
            <td>{tareLabel(v.tare_status)}</td>
            <td>{v.is_active ? 'Yes' : 'No'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function HaulierDriversTab({
  haulierEncId,
}: {
  haulierEncId: string;
}): JSX.Element {
  const q = useWbDrivers({ haulier_enc_id: haulierEncId, per_page: 100 });
  if (q.isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner label="Loading drivers" />
      </div>
    );
  }
  if (q.isError) {
    return <p className={styles.empty}>Could not load drivers.</p>;
  }
  const rows = q.data?.drivers ?? [];
  if (rows.length === 0) {
    return (
      <p className={styles.empty} data-testid="haulier-drivers-empty">
        No drivers linked to this haulier.
      </p>
    );
  }
  return (
    <table className={styles.subTable} data-testid="haulier-drivers-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Phone</th>
          <th>Active</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((d) => (
          <tr key={d.enc_id}>
            <td>
              <Link to={`/weighbridge/drivers/${d.enc_id}`}>{d.name}</Link>
            </td>
            <td>{d.phone ?? '—'}</td>
            <td>{d.is_active ? 'Yes' : 'No'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
